import React from 'react';
import { BookOpen } from 'lucide-react';

/**
 * ScholarlyPullQuote — elegant citation card for calculator result footers.
 *
 * Replaces the [bracketed footnote] style with a proper pull-quote:
 *   ┌────────────────────────────────────────────────────────────┐
 *   │ ❝ Quranic ayah or hadith text (Arabic if provided)         │
 *   │   Translation                                              │
 *   │   — SOURCE · MADHHAB                                       │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Props:
 *   arabic        – optional Arabic script (rendered with Amiri via font-arabic if class set)
 *   translation   – translated verse/hadith text
 *   source        – e.g. "Qur'an 4:11" / "Sahih al-Bukhari 2742"
 *   madhhab       – optional per-madhhab tag ("Hanafi" / "Jumhur" / "Ja'fari")
 *   tone          – 'light' (ivory card) | 'dark' (emerald card, use inside dark hero)
 *   testid        – data-testid
 */
export const ScholarlyPullQuote = ({
  arabic,
  translation,
  source,
  madhhab,
  tone = 'light',
  testid = 'scholarly-pullquote',
}) => {
  const isDark = tone === 'dark';
  return (
    <figure
      data-testid={testid}
      className={`relative overflow-hidden rounded-2xl border p-6 md:p-8 ${
        isDark
          ? 'bg-[#EDF5F1] border-gold-500/25 text-[#0D1520] backdrop-blur'
          : 'bg-gradient-to-br from-stone-50 to-[#F3EFEA] border-stone-200 text-stone-800'
      }`}
    >
      {/* Gold rail */}
      <div
        aria-hidden
        className={`absolute left-0 top-0 bottom-0 w-[3px] ${
          isDark ? 'bg-gold-400/70' : 'bg-gold-500/70'
        }`}
      />

      {arabic && (
        <div
          dir="rtl"
          lang="ar"
          className={`text-right font-arabic text-[22px] md:text-[26px] leading-loose mb-4 ${
            isDark ? 'text-gold-200' : 'text-[#0D1520]'
          }`}
        >
          {arabic}
        </div>
      )}

      {translation && (
        <blockquote
          className={`text-[15px] md:text-[16px] leading-relaxed italic ${
            isDark ? 'text-[#0D1520]' : 'text-stone-700'
          }`}
        >
          &ldquo;{translation}&rdquo;
        </blockquote>
      )}

      <figcaption
        className={`mt-4 pt-3 border-t flex items-center gap-2 text-[12px] font-mono uppercase tracking-[0.18em] ${
          isDark
            ? 'border-gold-500/20 text-gold-300'
            : 'border-stone-200 text-[#4A5568]'
        }`}
      >
        <BookOpen className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">
          {source}
          {madhhab && (
            <>
              <span className="opacity-40 mx-2">·</span>
              <span>{madhhab}</span>
            </>
          )}
        </span>
      </figcaption>
    </figure>
  );
};

export default ScholarlyPullQuote;
